import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  LineController,
  Tooltip,
  Legend,
} from "chart.js";
import dayjs from "dayjs";
import { minutesToDisplay } from "./timeUtils";
import { SITES } from "../sites";

// ── 세부 차트 운행/대기 툴팁 분리 플러그인 ──────────────────────
const detailTooltipSplitPlugin = {
  id: "detailTooltipSplit",
  beforeEvent(chart, { event }) {
    if (!chart.options.plugins?.detailRangeLines?.enabled) return;
    if (event.type !== "mousemove") return;

    const isHorizontal = chart.options.indexAxis === "y";
    const xScale = chart.scales.x;
    const yScale = chart.scales.y;

    // 1단계: 현재 호버된 행(가로) 또는 열(세로) 인덱스 파악
    const meta0 = chart.getDatasetMeta(0);
    let hoveredIndex = -1;
    let minAxisDist = Infinity;
    meta0.data.forEach((element, i) => {
      if (!element) return;
      const d = isHorizontal
        ? Math.abs(event.y - element.y)
        : Math.abs(event.x - element.x);
      if (d < minAxisDist) {
        minAxisDist = d;
        hoveredIndex = i;
      }
    });

    if (hoveredIndex < 0) return;

    // 2단계: 해당 인덱스에서만 운행/대기 avg 거리 비교
    let minDist = Infinity;
    let preferred = -1;
    [0, 1].forEach((dsIndex) => {
      if (!chart.isDatasetVisible(dsIndex)) return;
      const avg = chart.data.datasets[dsIndex].avgData?.[hoveredIndex];
      if (avg == null) return;
      const dist = isHorizontal
        ? Math.abs(event.x - xScale.getPixelForValue(avg))
        : Math.abs(event.y - yScale.getPixelForValue(avg));
      if (dist < minDist) {
        minDist = dist;
        preferred = dsIndex;
      }
    });

    chart._detailPreferredDs = preferred;
    chart._detailHoveredIndex = hoveredIndex;
  },
};

// ── 범위 선 + 원 플러그인 ─────────────────────────────────────
const detailRangeLinesPlugin = {
  id: "detailRangeLines",
  afterDatasetsDraw(chart) {
    const opts = chart.options.plugins?.detailRangeLines;
    if (!opts?.enabled) return;

    const { ctx, scales } = chart;
    const isHorizontal = opts.horizontal ?? false;
    const xScale = scales.x;
    const yScale = scales.y;
    const showMinMax = chart.isDatasetVisible(2);

    const drawCircle = (cx, cy, r, color) => {
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.restore();
    };

    [0, 1].forEach((dsIndex) => {
      if (!chart.isDatasetVisible(dsIndex)) return;
      const dataset = chart.data.datasets[dsIndex];
      const meta = chart.getDatasetMeta(dsIndex);

      meta.data.forEach((element, i) => {
        if (!element) return;
        const avg = dataset.avgData?.[i];
        const min = dataset.minData?.[i];
        const max = dataset.maxData?.[i];
        const avgColor = dataset.avgColor ?? "#003D48";
        const dotColor = dataset.dotColor ?? "#CCCCCC";

        if (!isHorizontal) {
          const cx = element.x;
          if (showMinMax) {
            if (min != null)
              drawCircle(cx, yScale.getPixelForValue(min), 4, dotColor);
            if (max != null)
              drawCircle(cx, yScale.getPixelForValue(max), 4, dotColor);
          }
          if (avg != null)
            drawCircle(cx, yScale.getPixelForValue(avg), 6, avgColor);
        } else {
          const cy = element.y;
          if (showMinMax) {
            if (min != null)
              drawCircle(xScale.getPixelForValue(min), cy, 4, dotColor);
            if (max != null)
              drawCircle(xScale.getPixelForValue(max), cy, 4, dotColor);
          }
          if (avg != null)
            drawCircle(xScale.getPixelForValue(avg), cy, 6, avgColor);
        }
      });
    });
  },
};

// Y축 상단에 가로 방향 타이틀 표기 플러그인
const yAxisHTitlePlugin = {
  id: "yAxisHTitle",
  afterDraw(chart) {
    const { ctx, scales } = chart;
    ["y", "y1"].forEach((id) => {
      const scale = scales[id];
      const title = scale?.options?.title;
      if (!scale || !title?.text) return;
      const isRight = scale.options.position === "right";
      ctx.save();
      ctx.font = `${title.font?.size ?? 11}px sans-serif`;
      ctx.fillStyle = title.color ?? "#666";
      ctx.textAlign = isRight ? "right" : "left";
      ctx.textBaseline = "bottom";
      ctx.fillText(
        title.text,
        isRight ? scale.right : scale.left,
        scale.top - 12,
      );
      ctx.restore();
    });
  },
};

// ── 세부 차트 tooltip 위치 positioner ─────────────────────────
// preferred 데이터셋의 avg 점 위치에 caret이 오도록 tooltip을 배치한다.
Tooltip.positioners.detailSplit = function (items, eventPosition) {
  if (!items.length) return false;
  const chart = this.chart;

  // detailRangeLines가 비활성화된 차트는 기본 positioner로 fallback
  if (!chart.options.plugins?.detailRangeLines?.enabled) {
    return Tooltip.positioners.average.call(this, items, eventPosition);
  }

  const preferred = chart._detailPreferredDs;
  const i = chart._detailHoveredIndex ?? items[0]?.index;
  const ds =
    preferred != null && preferred >= 0 ? chart.data.datasets[preferred] : null;

  // preferred가 유효하지 않으면 fallback
  if (!ds || i == null || i < 0) {
    return Tooltip.positioners.average.call(this, items, eventPosition);
  }

  const element = chart.getDatasetMeta(preferred).data[i];
  const isHorizontal = chart.options.indexAxis === "y";

  if (isHorizontal) {
    const avg = ds.avgData?.[i];
    return {
      x: avg != null ? chart.scales.x.getPixelForValue(avg) : eventPosition.x,
      y: element?.y ?? eventPosition.y,
    };
  }
  const avg = ds.avgData?.[i];
  return {
    x: element?.x ?? eventPosition.x,
    y: avg != null ? chart.scales.y.getPixelForValue(avg) : eventPosition.y,
  };
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  LineController,
  detailRangeLinesPlugin,
  detailTooltipSplitPlugin, // Tooltip보다 먼저 등록해야 beforeEvent에서 _detailPreferredDs가 먼저 세팅됨
  Tooltip,
  Legend,
  yAxisHTitlePlugin,
);

// ── 단지 / 디바이스 ────────────────────────────────────────────
export const COMPLEX_OPTIONS = [
  { value: "", label: "단지 전체" },
  ...SITES.map((s) => ({ value: s.deviceId, label: s.label })),
];

// ── 색상 ──────────────────────────────────────────────────────
export const COLOR_ATTEMPT = "#00CAE0"; // 배송 시도 횟수
export const COLOR_VALID = "#007F8C"; // 유효 배송 횟수
export const COLOR_DRIVE = "#003D48"; // 운행 시간 합계
export const COLOR_WAIT = "#007F8C"; // 대기 시간
export const COLOR_ARRIVAL = "#005D66"; // 배송 목적지 도달률 라인
export const COLOR_TOTAL = "#009DAD"; // 시간 합계 (디바이스별)

const RADIUS_TOP = { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 };
const RADIUS_RIGHT = { topRight: 4, bottomRight: 4, topLeft: 0, bottomLeft: 0 };

// ── 조회 기간 제한 ─────────────────────────────────────────────
export const disabledDate = (current, { from } = {}) => {
  const sixMonthsAgo = dayjs().subtract(6, "month").startOf("day");
  const yesterday = dayjs().subtract(1, "day").endOf("day");
  if (current.isBefore(sixMonthsAgo) || current.isAfter(yesterday)) return true;
  if (from) return Math.abs(current.diff(from, "day")) >= 30;
  return false;
};

// ── 포매터 ────────────────────────────────────────────────────
export const roundCount = (v) => (v == null ? 0 : Math.round(v * 100) / 100);
export const roundPercent = (v) => (v == null ? null : Math.round(v * 10) / 10);

// ── KPI 툴팁 문구 ──────────────────────────────────────────────
export const TOOLTIPS = {
  validDelivery:
    "조회 기간 내 수행된 모든 배송 작업중 로봇이 실제로 물류 기능을 수행한 정도를 횟수로 환산한 지표입니다. 각 배송 작업에서 유효 지수 (배송 작업에서 계획된 모든 이동 작업 중 성공한 이동 작업의 비율)를 누적 합산하여 산출합니다. 하단 값은 조회 기간 내 수행된 모든 배송 작업 횟수입니다.",
  arrivalRate:
    "전체 계획된 배송지(동) 이동 시도 중 로봇이 실제 배송지 도착에 성공한 비율을 백분율로 환산한 지표입니다.",
  cycleTime:
    "조회 기간 동안 로봇이 수행한 모든 배송 작업의 총 소요 시간입니다.",
  cycleTimeIntegrated:
    "조회 기간 동안 수행된 모든 배송 작업의 총 소요 시간입니다.",
  driveTime:
    "배송 작업 중 로봇이 목적지(동/배송센터)로 순수하게 이동한 시간의 총합입니다.",
  waitTime:
    "배송 작업 중 이동이 완료된 후, 다음 이동이 시작되기 전까지 로봇이 멈춰 있던 시간의 총합입니다.",
};

// ── 범례 아이템 ──────────────────────────────────────────────
export const LEGEND_DELIVERY_COUNT = [
  { color: COLOR_ATTEMPT, label: "배송 시도 횟수" },
  { color: COLOR_VALID, label: "유효 배송 횟수" },
  { color: COLOR_ARRIVAL, label: "배송 목적지 도달률" },
];
export const LEGEND_STACKED_TIME = [
  { color: COLOR_DRIVE, label: "운행 시간" },
  { color: COLOR_WAIT, label: "대기 시간" },
];
export const LEGEND_DEVICE_TIME = [
  { color: COLOR_TOTAL, label: "합계" },
  { color: COLOR_WAIT, label: "평균" },
];
export const LEGEND_DEST_COUNT = [
  { color: COLOR_ATTEMPT, label: "이동 시도 횟수" },
  { color: COLOR_ARRIVAL, label: "이동 성공률" },
];
export const LEGEND_HSTACKED_TIME = [
  { color: COLOR_DRIVE, label: "운행 시간" },
  { color: COLOR_WAIT, label: "대기 시간" },
];
export const LEGEND_DETAIL_TIME = [
  { color: "#003D48", label: "평균 운행 시간", datasetIndices: [0] },
  { color: "#007F8C", label: "평균 대기 시간", datasetIndices: [1] },
  { color: "#CCCCCC", label: "각 지표별 최대·최소값", datasetIndices: [2] },
];
export const LEGEND_INTEGRATED_CYCLE = [
  { color: "#009DAD", label: "합계" },
  { color: "#D6D4CC", label: "평균" },
];
export const LEGEND_INTEGRATED_DRIVE = [
  { color: "#003D48", label: "합계" },
  { color: "#D6D4CC", label: "평균" },
];
export const LEGEND_INTEGRATED_WAIT = [
  { color: "#007F8C", label: "합계" },
  { color: "#D6D4CC", label: "평균" },
];

// ── 차트 reset 헬퍼 ───────────────────────────────────────────
export const resetChartLegend = (chartRef) => {
  const chart = chartRef?.current;
  if (!chart) return;
  chart.data.datasets.forEach((_, i) => chart.setDatasetVisibility(i, true));
  chart.update();
};

// ── 배송 횟수 차트 (막대 + 꺾은선) ───────────────────────────
export const buildDeliveryCountData = (
  labels,
  attemptCounts,
  validCounts,
  arrivalRates,
  attemptColor = COLOR_ATTEMPT,
  validColor = COLOR_VALID,
  arrivalColor = COLOR_ARRIVAL,
) => ({
  labels,
  datasets: [
    {
      type: "bar",
      label: "배송 시도 횟수",
      data: attemptCounts,
      backgroundColor: attemptColor,
      borderRadius: RADIUS_TOP,
      borderSkipped: false,
      yAxisID: "y",
      order: 2,
    },
    {
      type: "bar",
      label: "유효 배송 횟수",
      data: validCounts.map(roundCount),
      backgroundColor: validColor,
      borderRadius: RADIUS_TOP,
      borderSkipped: false,
      yAxisID: "y",
      order: 3,
    },
    {
      type: "line",
      label: "배송 목적지 도달률",
      data: arrivalRates.map(roundPercent),
      borderColor: arrivalColor,
      backgroundColor: arrivalColor,
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2,
      spanGaps: false,
      yAxisID: "y1",
      order: 1,
    },
  ],
});

export const buildDeliveryCountOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  clip: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: false, labels: { boxWidth: 12, font: { size: 12 } } },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          if (ctx.dataset.yAxisID === "y1")
            return `${ctx.dataset.label}: ${ctx.parsed.y ?? "-"}%`;
          if (ctx.dataset.label === "유효 배송 횟수")
            return `${ctx.dataset.label}: ${ctx.parsed.y != null ? ctx.parsed.y.toFixed(2) : "-"}회`;
          return `${ctx.dataset.label}: ${ctx.parsed.y ?? "-"}회`;
        },
      },
    },
  },
  scales: {
    x: { grid: { display: false } },
    y: {
      beginAtZero: true,
      position: "left",
      ticks: { callback: (v) => `${v}회` },
    },
    y1: {
      beginAtZero: true,
      position: "right",
      min: 0,
      max: 100,
      grid: { drawOnChartArea: false },
      ticks: { callback: (v) => `${v}%` },
    },
  },
});

// ── 세로 누적 막대 차트 (일별/요일별 배송 시간) ──────────────
export const buildStackedBarData = (labels, driveTotals, waitTotals) => ({
  labels,
  datasets: [
    {
      label: "운행 시간",
      data: driveTotals,
      backgroundColor: COLOR_DRIVE,
      borderRadius: 0,
      borderSkipped: false,
      stack: "s",
    },
    {
      label: "대기 시간",
      data: waitTotals,
      backgroundColor: COLOR_WAIT,
      borderRadius: RADIUS_TOP,
      borderSkipped: false,
      stack: "s",
    },
  ],
});

export const buildStackedBarOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: false, labels: { boxWidth: 12, font: { size: 12 } } },
    tooltip: {
      callbacks: {
        label: (ctx) =>
          `${ctx.dataset.label}: ${minutesToDisplay(ctx.parsed.y)}`,
      },
    },
  },
  scales: {
    x: { grid: { display: false }, stacked: true },
    y: {
      beginAtZero: true,
      stacked: true,
      ticks: { callback: (v) => `${v}분` },
    },
  },
});

// ── 가로 누적 막대 차트 (목적지별 배송 시간) ─────────────────
export const buildHStackedBarData = (labels, driveTotals, waitTotals) => ({
  labels,
  datasets: [
    {
      label: "운행 시간",
      data: driveTotals,
      backgroundColor: COLOR_DRIVE,
      stack: "s",
    },
    {
      label: "대기 시간",
      data: waitTotals,
      backgroundColor: COLOR_WAIT,
      borderRadius: RADIUS_RIGHT,
      stack: "s",
    },
  ],
});

export const buildHStackedBarOptions = () => ({
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: false, labels: { boxWidth: 12, font: { size: 12 } } },
    tooltip: {
      callbacks: {
        label: (ctx) =>
          `${ctx.dataset.label}: ${minutesToDisplay(ctx.parsed.x)}`,
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      stacked: true,
      ticks: { callback: (v) => `${v}분` },
    },
    y: { grid: { display: false }, stacked: true },
  },
});

// ── 점 범위 차트 (일별/요일별 배송 시간 상세) ─────────────────
export const buildDetailTimeData = (
  labels,
  driveAvgs,
  driveMaxs,
  driveMins,
  waitAvgs,
  waitMaxs,
  waitMins,
  driveColor = "#003D48",
  waitColor = "#007F8C",
  rangeColor = "#F0F0F0",
  dotColor = "#CCCCCC",
) => ({
  labels,
  datasets: [
    {
      type: "bar",
      label: "평균 운행 시간",
      data: labels.map((_, i) =>
        driveMins[i] != null && driveMaxs[i] != null
          ? [driveMins[i], driveMaxs[i]]
          : null,
      ),
      backgroundColor: rangeColor,
      borderColor: rangeColor,
      maxBarThickness: 2,
      borderSkipped: false,
      order: 10,
      avgData: driveAvgs,
      minData: driveMins,
      maxData: driveMaxs,
      avgColor: driveColor,
      dotColor,
    },
    {
      type: "bar",
      label: "평균 대기 시간",
      data: labels.map((_, i) =>
        waitMins[i] != null && waitMaxs[i] != null
          ? [waitMins[i], waitMaxs[i]]
          : null,
      ),
      backgroundColor: rangeColor,
      borderColor: rangeColor,
      maxBarThickness: 2,
      borderSkipped: false,
      order: 10,
      avgData: waitAvgs,
      minData: waitMins,
      maxData: waitMaxs,
      avgColor: waitColor,
      dotColor,
    },
    {
      type: "line",
      label: "각 지표별 최대·최소값",
      data: [],
      backgroundColor: dotColor,
      showLine: false,
      pointRadius: 3,
    },
  ],
});

export const buildDetailTimeOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      position: "detailSplit",
      displayColors: false,
      callbacks: {
        label: (ctx) => {
          const ds = ctx.dataset;
          const i = ctx.dataIndex;
          if (!ds.avgData) return null;
          const avg = ds.avgData[i];
          if (avg == null) return null;

          const chart = ctx.chart;
          const otherDsIndex = ctx.datasetIndex === 0 ? 1 : 0;
          const otherAvg = chart.data.datasets[otherDsIndex]?.avgData?.[i];

          if (otherAvg != null && chart.isDatasetVisible(otherDsIndex)) {
            let preferred = chart._detailPreferredDs ?? -1;
            // 플러그인이 다른 인덱스에서 계산했거나 미설정인 경우, 이벤트 위치로 직접 계산
            if (preferred === -1 || chart._detailHoveredIndex !== i) {
              const eventPos = chart.tooltip?._eventPosition;
              if (eventPos != null) {
                const yScale = chart.scales.y;
                const myDist = Math.abs(
                  eventPos.y - yScale.getPixelForValue(avg),
                );
                const otherDist = Math.abs(
                  eventPos.y - yScale.getPixelForValue(otherAvg),
                );
                preferred =
                  myDist <= otherDist ? ctx.datasetIndex : otherDsIndex;
              } else {
                preferred = 0;
              }
            }
            if (ctx.datasetIndex !== preferred) return null;
          }

          const min = ds.minData?.[i];
          const max = ds.maxData?.[i];
          const prefix = ds.label.includes("운행") ? "운행" : "대기";
          const lines = [];
          lines.push(`평균 ${prefix} 시간: ${minutesToDisplay(avg)}/회`);
          if (max != null)
            lines.push(`최대 ${prefix} 시간: ${minutesToDisplay(max)}`);
          if (min != null)
            lines.push(`최소 ${prefix} 시간: ${minutesToDisplay(min)}`);
          return lines;
        },
      },
    },
    detailRangeLines: { enabled: true, horizontal: false },
  },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true, ticks: { callback: (v) => `${v}분` } },
  },
});

// ── 가로 점 범위 차트 (목적지별 배송 시간 상세) ──────────────
export const buildHDetailTimeData = (
  labels,
  driveAvgs,
  driveMaxs,
  driveMins,
  waitAvgs,
  waitMaxs,
  waitMins,
  driveColor = "#003D48",
  waitColor = "#007F8C",
  rangeColor = "#F0F0F0",
  dotColor = "#CCCCCC",
) => ({
  labels,
  datasets: [
    {
      type: "bar",
      label: "평균 운행 시간",
      data: labels.map((_, i) =>
        driveMins[i] != null && driveMaxs[i] != null
          ? [driveMins[i], driveMaxs[i]]
          : null,
      ),
      backgroundColor: rangeColor,
      borderColor: rangeColor,
      maxBarThickness: 2,
      borderSkipped: false,
      order: 10,
      avgData: driveAvgs,
      minData: driveMins,
      maxData: driveMaxs,
      avgColor: driveColor,
      dotColor,
    },
    {
      type: "bar",
      label: "평균 대기 시간",
      data: labels.map((_, i) =>
        waitMins[i] != null && waitMaxs[i] != null
          ? [waitMins[i], waitMaxs[i]]
          : null,
      ),
      backgroundColor: rangeColor,
      borderColor: rangeColor,
      maxBarThickness: 2,
      borderSkipped: false,
      order: 10,
      avgData: waitAvgs,
      minData: waitMins,
      maxData: waitMaxs,
      avgColor: waitColor,
      dotColor,
    },
    {
      type: "line",
      label: "각 지표별 최대·최소값",
      data: [],
      backgroundColor: dotColor,
      showLine: false,
      pointRadius: 3,
    },
  ],
});

export const buildHDetailTimeOptions = () => ({
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", axis: "y", intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      position: "detailSplit",
      displayColors: false,
      callbacks: {
        label: (ctx) => {
          const ds = ctx.dataset;
          const i = ctx.dataIndex;
          if (!ds.avgData) return null;
          const avg = ds.avgData[i];
          if (avg == null) return null;

          const chart = ctx.chart;
          const otherDsIndex = ctx.datasetIndex === 0 ? 1 : 0;
          const otherAvg = chart.data.datasets[otherDsIndex]?.avgData?.[i];

          if (otherAvg != null && chart.isDatasetVisible(otherDsIndex)) {
            let preferred = chart._detailPreferredDs ?? -1;
            if (preferred === -1 || chart._detailHoveredIndex !== i) {
              const eventPos = chart.tooltip?._eventPosition;
              if (eventPos != null) {
                const xScale = chart.scales.x;
                const myDist = Math.abs(
                  eventPos.x - xScale.getPixelForValue(avg),
                );
                const otherDist = Math.abs(
                  eventPos.x - xScale.getPixelForValue(otherAvg),
                );
                preferred =
                  myDist <= otherDist ? ctx.datasetIndex : otherDsIndex;
              } else {
                preferred = 0;
              }
            }
            if (ctx.datasetIndex !== preferred) return null;
          }

          const min = ds.minData?.[i];
          const max = ds.maxData?.[i];
          const prefix = ds.label.includes("운행") ? "운행" : "대기";
          const lines = [];
          lines.push(`평균 ${prefix} 시간: ${minutesToDisplay(avg)}/회`);
          if (max != null)
            lines.push(`최대 ${prefix} 시간: ${minutesToDisplay(max)}`);
          if (min != null)
            lines.push(`최소 ${prefix} 시간: ${minutesToDisplay(min)}`);
          return lines;
        },
      },
    },
    detailRangeLines: { enabled: true, horizontal: true },
  },
  scales: {
    x: { beginAtZero: true, ticks: { callback: (v) => `${v}분` } },
    y: { grid: { display: false } },
  },
});

// ── 목적지별 이동 횟수 차트 (가로 막대 + 성공률 라인) ─────────
export const buildDestCountData = (
  labels,
  moveCounts,
  successRates,
  attemptColor = COLOR_ATTEMPT,
  arrivalColor = COLOR_ARRIVAL,
) => ({
  labels,
  datasets: [
    {
      type: "bar",
      label: "이동 시도 횟수",
      data: moveCounts,
      backgroundColor: attemptColor,
      borderRadius: RADIUS_TOP,
      borderSkipped: false,
      yAxisID: "y",
      order: 2,
    },
    {
      type: "line",
      label: "이동 성공률",
      data: successRates.map(roundPercent),
      borderColor: arrivalColor,
      backgroundColor: arrivalColor,
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2,
      spanGaps: false,
      yAxisID: "y1",
      order: 1,
    },
  ],
});

export const buildDestCountOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  clip: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: false, labels: { boxWidth: 12, font: { size: 12 } } },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          if (ctx.dataset.yAxisID === "y1")
            return `${ctx.dataset.label}: ${ctx.parsed.y ?? "-"}%`;
          return `${ctx.dataset.label}: ${ctx.parsed.y ?? "-"}회`;
        },
      },
    },
  },
  scales: {
    x: { grid: { display: false } },
    y: {
      beginAtZero: true,
      position: "left",
      ticks: { callback: (v) => `${v}회` },
    },
    y1: {
      beginAtZero: true,
      position: "right",
      min: 0,
      max: 100,
      grid: { drawOnChartArea: false },
      ticks: { callback: (v) => `${v}%` },
    },
  },
});

// ── 디바이스별 시간 차트 (통합 대시보드 - 합계/평균 그룹 막대) ─
export const buildDeviceTimeData = (
  labels,
  totals,
  avgs,
  totalColor = COLOR_TOTAL,
  avgColor = COLOR_WAIT,
) => ({
  labels,
  datasets: [
    {
      label: "합계",
      data: totals,
      backgroundColor: totalColor,
      borderRadius: RADIUS_TOP,
      borderSkipped: false,
      yAxisID: "y",
    },
    {
      label: "평균",
      data: avgs,
      backgroundColor: avgColor,
      borderRadius: RADIUS_TOP,
      borderSkipped: false,
      yAxisID: "y1",
    },
  ],
});

export const buildDeviceTimeOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: { top: 30 } },
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: false, labels: { boxWidth: 12, font: { size: 12 } } },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          if (ctx.parsed.y == null) return `${ctx.dataset.label}: -`;
          const suffix = ctx.dataset.yAxisID === "y1" ? "/회" : "";
          return `${ctx.dataset.label}: ${minutesToDisplay(ctx.parsed.y)}${suffix}`;
        },
      },
    },
  },
  scales: {
    x: { grid: { display: false } },
    y: {
      beginAtZero: true,
      position: "left",
      title: { display: false, text: "합계", font: { size: 11 } },
      ticks: { callback: (v) => `${v}분` },
    },
    y1: {
      beginAtZero: true,
      position: "right",
      grid: { drawOnChartArea: false },
      title: { display: false, text: "평균", font: { size: 11 } },
      ticks: { callback: (v) => `${v}분` },
    },
  },
});

// ── 단순 막대 차트 (시간대별 배송 시도 횟수 등) ──────────────
export const buildSimpleBarData = (labels, data, color = COLOR_ATTEMPT) => ({
  labels,
  datasets: [
    {
      data,
      backgroundColor: color,
      borderRadius: RADIUS_TOP,
      borderSkipped: false,
    },
  ],
});

export const buildSimpleBarOptions = (tooltipLabel = (v) => `${v}회`) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: (ctx) =>
          ctx.parsed.y == null ? null : tooltipLabel(ctx.parsed.y),
      },
    },
  },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true },
  },
});
